import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { metaTagService } from '../../services/metaTagService';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';

const MetaTagManagementPage = () => {
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, tag: null });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '',
    is_active: true
  });

  useEffect(() => {
    fetchTags();
  }, [activeFilter]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      let params = {};
      if (activeFilter === 'active') {
        params.is_active = true;
      } else if (activeFilter === 'inactive') {
        params.is_active = false;
      }
      
      console.log('Fetching tags with params:', params);
      const response = await metaTagService.getTagsWithFilter(params.is_active);
      console.log('Tags response:', response);
      
      // Ensure response is an array
      if (Array.isArray(response)) {
        setTags(response);
      } else if (response && Array.isArray(response.results)) {
        setTags(response.results);
      } else {
        console.error('Unexpected response format:', response);
        setTags([]);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      let errorMessage = 'Failed to fetch tags';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Admin privileges required.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setFormData({
        name: tag.name,
        description: tag.description || '',
        color: tag.color || '',
        is_active: tag.is_active
      });
    } else {
      setEditingTag(null);
      setFormData({
        name: '',
        description: '',
        color: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTag(null);
    setFormData({
      name: '',
      description: '',
      color: '',
      is_active: true
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingTag) {
        await metaTagService.update(editingTag.id, formData);
        setSnackbar({
          open: true,
          message: 'Tag updated successfully',
          severity: 'success'
        });
      } else {
        await metaTagService.create(formData);
        setSnackbar({
          open: true,
          message: 'Tag created successfully',
          severity: 'success'
        });
      }
      
      // Clear the tag cache so TagSelector components get fresh data
      metaTagService.clearCache();
      
      handleCloseDialog();
      fetchTags();
    } catch (error) {
      console.error('Failed to save tag:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to save tag',
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.tag) return;
    
    try {
      await metaTagService.delete(deleteConfirm.tag.id);
      
      // Clear the tag cache so TagSelector components get fresh data
      metaTagService.clearCache();
      
      setSnackbar({
        open: true,
        message: 'Tag deleted successfully',
        severity: 'success'
      });
      setDeleteConfirm({ open: false, tag: null });
      fetchTags();
    } catch (error) {
      console.error('Failed to delete tag:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete tag',
        severity: 'error'
      });
    }
  };

  const filteredTags = (Array.isArray(tags) ? tags : []).filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleColorChange = (event) => {
    setFormData(prev => ({ ...prev, color: event.target.value }));
  };

  return (
    <AdminLayoutWrapper>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 4, mb: 3 }}>
          Meta Tags Management
        </Typography>
        
        {/* Summary Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card sx={{ minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" component="h2" color="text.secondary">
                  Total Tags
                </Typography>
                <Typography variant="h4" color="primary">
                  {Array.isArray(tags) ? tags.length : 0}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" component="h2" color="text.secondary">
                  Active Tags
                </Typography>
                <Typography variant="h4" color="success.main">
                  {Array.isArray(tags) ? tags.filter(tag => tag.is_active).length : 0}
                </Typography>
              </CardContent>
            </Card>
                         <Card sx={{ minWidth: 200 }}>
               <CardContent>
                 <Typography variant="h6" component="h2" color="text.secondary">
                   Inactive Tags
                 </Typography>
                 <Typography variant="h4" color="warning.main">
                   {Array.isArray(tags) ? tags.filter(tag => !tag.is_active).length : 0}
                 </Typography>
               </CardContent>
             </Card>
             <Card sx={{ minWidth: 200 }}>
               <CardContent>
                 <Typography variant="h6" component="h2" color="text.secondary">
                   Protected Tags
                 </Typography>
                 <Typography variant="h4" color="info.main">
                   {Array.isArray(tags) ? tags.filter(tag => tag.questions_count > 0).length : 0}
                 </Typography>
                 <Typography variant="caption" color="text.secondary">
                   Connected to questions
                 </Typography>
               </CardContent>
             </Card>
          </Box>
        </Box>

        {/* Action Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ mb: 2 }}
          >
            Add New Tag
          </Button>
        </Box>

        {/* Filters and Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search tags by name or slug..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ minWidth: 300 }}
                size="small"
              />
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Tags</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

                                 {/* Tags Table */}
         <Card>
           <CardContent sx={{ p: 0 }}>
             <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}>
               <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <InfoIcon fontSize="small" />
                 Tags connected to questions are protected from deletion. Remove question associations first to delete protected tags.
               </Typography>
             </Box>
             <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Slug</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Color</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Questions</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Updated</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Loading tags...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredTags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? 'No tags match your search' : 'No tags found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTags.map((tag) => (
                      <TableRow 
                        key={tag.id}
                        sx={{ 
                          '&:hover': { backgroundColor: 'grey.50' },
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {tag.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={tag.slug} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              backgroundColor: 'primary.50',
                              borderColor: 'primary.main',
                              color: 'primary.main'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {tag.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {tag.color && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  backgroundColor: tag.color,
                                  borderRadius: 1,
                                  border: '1px solid #ddd'
                                }}
                              />
                              <Typography variant="body2" fontFamily="monospace">
                                {tag.color}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tag.is_active ? 'Active' : 'Inactive'}
                            color={tag.is_active ? 'success' : 'default'}
                            size="small"
                            variant={tag.is_active ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                                           <TableCell>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       <Typography variant="body2" fontWeight="medium">
                         {tag.questions_count || 0}
                       </Typography>
                       {tag.questions_count > 0 && (
                         <Chip
                           label="Protected"
                           size="small"
                           color="warning"
                           variant="outlined"
                           sx={{ 
                             fontSize: '0.7rem',
                             height: 20,
                             '& .MuiChip-label': { px: 1 }
                           }}
                         />
                       )}
                     </Box>
                   </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(tag.updated_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit Tag">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(tag)}
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'primary.50' }
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                                                   <Tooltip 
                         title={tag.questions_count > 0 
                           ? `Cannot delete tag "${tag.name}" - it is currently used by ${tag.questions_count} question(s). Remove all question associations first.`
                           : "Delete Tag"
                         }
                       >
                         <span> {/* Wrapper needed for disabled IconButton tooltip */}
                           <IconButton
                             size="small"
                             color="error"
                             onClick={() => setDeleteConfirm({ open: true, tag })}
                             disabled={tag.questions_count > 0}
                             sx={{ 
                               '&:hover': { 
                                 backgroundColor: tag.questions_count > 0 ? 'transparent' : 'error.50' 
                               },
                               opacity: tag.questions_count > 0 ? 0.5 : 1,
                               cursor: tag.questions_count > 0 ? 'not-allowed' : 'pointer'
                             }}
                           >
                             <DeleteIcon />
                           </IconButton>
                         </span>
                       </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant="h6" component="h2">
            {editingTag ? 'Edit Meta Tag' : 'Add New Meta Tag'}
          </Typography>
        </DialogTitle>
                 <DialogContent sx={{ pt: 4 }}>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
             <TextField
               fullWidth
               label="Tag Name"
               value={formData.name}
               onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
               required
               helperText="Unique name for the tag (case-insensitive)"
               size="small"
             />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              helperText="Optional description of what this tag represents"
              size="small"
            />
            <TextField
              fullWidth
              label="Color"
              value={formData.color}
              onChange={handleColorChange}
              placeholder="#FF5733"
              helperText="Optional hex color code for UI display"
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  color="primary"
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTag ? 'Update Tag' : 'Create Tag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirm.open} 
        onClose={() => setDeleteConfirm({ open: false, tag: null })}
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant="h6" component="h2" color="error">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>"{deleteConfirm.tag?.name}"</strong>?
          </Typography>
          {deleteConfirm.tag && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This tag is currently used by <strong>{deleteConfirm.tag.questions_count || 0} question(s)</strong>.
                Deleting will remove all associations but keep the questions intact.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button onClick={() => setDeleteConfirm({ open: false, tag: null })} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete Tag
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': { fontWeight: 500 }
          }}
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </AdminLayoutWrapper>
  );
};

export default MetaTagManagementPage;
